import React from 'react';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import styles from './campaignBuilder.module.css';

/**
 * Progressive-disclosure primitives for the Campaign Builder admin.
 *
 * `CollapsibleSection` is the backbone of the redesigned admin: every group of fields / every embedded
 * editor lives inside one, so an author sees only the essentials by default and expands a section to
 * configure it. It is a hand-rolled, `--cb-*`-themed accordion (not MUI's, which clashed with the
 * builder aesthetic and rendered oddly in the dock). Uncontrolled by default (seeded from `defaultOpen`);
 * pass `open`/`onToggle` to control it. Header actions stop propagation so they don't toggle the section.
 *
 * `FullScreenOverlay` hosts a page-scale tool (the report builder) launched from the narrow dock — it
 * escapes the dock's width/scroll entirely and gives the tool the room it was designed for.
 *
 * `SummaryRow` is a compact collapsed entry point: a title + meta + a launch/expand control, used where a
 * heavyweight tool should not render inline (again, the report builder).
 */

function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(' ');
}

export function CollapsibleSection(props: {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  /** Right-aligned summary chip (e.g. a count) shown in the header. */
  badge?: React.ReactNode;
  /** Right-aligned header controls; clicks here don't toggle the section. */
  actions?: React.ReactNode;
  defaultOpen?: boolean;
  /** Controlled open state (optional). When set, `onToggle` should update it. */
  open?: boolean;
  onToggle?: (open: boolean) => void;
  /** Soft accent left border, for the kind-specific / active-context section. */
  accent?: boolean;
  children: React.ReactNode;
}): JSX.Element {
  const controlled = props.open !== undefined;
  const [internalOpen, setInternalOpen] = React.useState(!!props.defaultOpen);
  const open = controlled ? !!props.open : internalOpen;

  const toggle = () => {
    const next = !open;
    if (!controlled) setInternalOpen(next);
    props.onToggle?.(next);
  };

  return (
    <div className={cx(styles.disclosure, open && styles.disclosureOpen, props.accent && styles.disclosureAccent)}>
      <button type="button" className={styles.disclosureHead} onClick={toggle} aria-expanded={open}>
        <span className={cx(styles.disclosureChevron, open && styles.disclosureChevronOpen)}>
          <ChevronRightIcon fontSize="small" />
        </span>
        <span className={styles.disclosureTitleWrap}>
          <span className={styles.disclosureTitle}>{props.title}</span>
          {props.subtitle != null && <span className={styles.disclosureSub}>{props.subtitle}</span>}
        </span>
        {props.badge != null && <span className={styles.disclosureBadge}>{props.badge}</span>}
        {props.actions != null && (
          <span
            className={styles.disclosureHeadActions}
            onClick={(e) => e.stopPropagation()}
            role="presentation"
          >
            {props.actions}
          </span>
        )}
      </button>
      {open && <div className={styles.disclosureBody}>{props.children}</div>}
    </div>
  );
}

/** A compact collapsed entry point: title + meta + a launch/expand control on the right. */
export function SummaryRow(props: {
  title: React.ReactNode;
  meta?: React.ReactNode;
  action: React.ReactNode;
}): JSX.Element {
  return (
    <div className={styles.summaryRow}>
      <div className={styles.summaryRowText}>
        <p className={styles.summaryRowTitle}>{props.title}</p>
        {props.meta != null && <p className={styles.summaryRowMeta}>{props.meta}</p>}
      </div>
      {props.action}
    </div>
  );
}

/** Full-screen overlay for a page-scale tool launched from the dock. Renders its own header + scroll. */
export function FullScreenOverlay(props: {
  title: React.ReactNode;
  onClose: () => void;
  children: React.ReactNode;
}): JSX.Element {
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') props.onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [props]);

  return (
    <>
      <div className={styles.fullScreenScrim} onClick={props.onClose} aria-hidden />
      <div className={styles.fullScreenPanel} role="dialog" aria-modal="true">
        <div className={styles.fullScreenHead}>
          <h3 className={styles.fullScreenTitle}>{props.title}</h3>
          <button
            type="button"
            className={cx(styles.btn, styles.ghost)}
            style={{ marginLeft: 'auto' }}
            onClick={props.onClose}
          >
            Done
          </button>
        </div>
        <div className={styles.fullScreenBody}>{props.children}</div>
      </div>
    </>
  );
}

export default CollapsibleSection;
